import type { Axios } from "axios";
import { function as function_, taskEither } from "fp-ts";

import { InquiryServicePorts } from "../../model";

import { URLS } from "./consts";
import { Common, Create, Delete, Get, GetMany, GetPaginated, Resolve, Update } from "./ios";
import { mapDtoToEntity } from "./mappers";

import { Request, Response } from "~/shared/api";
import { AuthenticationError, NotFoundError, UnexpectedError } from "~/shared/error";
import { Pagination } from "~/shared/zod";

class ApiProvider extends InquiryServicePorts.Provider {
  private readonly axios: Axios;

  constructor(axios: Axios) {
    super();
    this.axios = axios;
  }

  override create({
    subject,
    contacts,
    ...params
  }: InquiryServicePorts.ProviderIos.Create.In): taskEither.TaskEither<UnexpectedError, void> {
    return function_.pipe(
      Response.process(
        () =>
          this.axios.post(URLS.create, {
            subject: Common.NUMBER_BY_SUBJECT[subject],
            ...contacts,
            ...params,
          }),
        Create.zRes,
      ),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 200) return taskEither.right(void 0);

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override update(
    params: InquiryServicePorts.ProviderIos.Update.In,
  ): taskEither.TaskEither<
    UnexpectedError | AuthenticationError | NotFoundError,
    InquiryServicePorts.ProviderIos.Common.OutSchema
  > {
    return function_.pipe(
      Response.process(() => this.axios.put(URLS.update, params), Update.zRes),
      taskEither.flatMap((response) => {
        const { status, body } = response;
        if (status === 200) return taskEither.right(mapDtoToEntity(body.data));

        if (status === 400 && body.errorDetails.codeErrors.includes("HTTP_NOT_FOUND"))
          return taskEither.left(new NotFoundError());

        if (status === 401) return taskEither.left(new AuthenticationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override delete({
    id,
  }: InquiryServicePorts.ProviderIos.Delete.In): taskEither.TaskEither<
    UnexpectedError | AuthenticationError | NotFoundError,
    void
  > {
    return function_.pipe(
      Response.process(() => this.axios.delete(URLS.delete(id)), Delete.zRes),
      taskEither.flatMap((response) => {
        const { status } = response;
        if (status === 204) return taskEither.right(void 0);

        if (status === 401) return taskEither.left(new AuthenticationError());

        if (status === 404) return taskEither.left(new NotFoundError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override resolve({
    id,
    ...params
  }: InquiryServicePorts.ProviderIos.Resolve.In): taskEither.TaskEither<
    UnexpectedError | AuthenticationError | NotFoundError,
    void
  > {
    return function_.pipe(
      Response.process(
        () =>
          this.axios.post(URLS.resolve, {
            ...params,
            inquiryId: id,
          }),
        Resolve.zRes,
      ),
      taskEither.flatMap((response) => {
        const { status, body } = response;
        if (status === 200) return taskEither.right(void 0);

        if (status === 400 && body.errorDetails.codeErrors.includes("HTTP_NOT_FOUND"))
          return taskEither.left(new NotFoundError());

        if (status === 401) return taskEither.left(new AuthenticationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override get({
    id,
  }: InquiryServicePorts.ProviderIos.Get.In): taskEither.TaskEither<
    UnexpectedError | AuthenticationError | NotFoundError,
    InquiryServicePorts.ProviderIos.Common.OutSchema
  > {
    return function_.pipe(
      Response.process(() => this.axios.get(URLS.get(id)), Get.zRes),
      taskEither.flatMap((response) => {
        const { status, body } = response;
        if (status === 200) return taskEither.right(mapDtoToEntity(body.data));

        if (status === 401) return taskEither.left(new AuthenticationError());

        if (status === 404) return taskEither.left(new NotFoundError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override getMany(): taskEither.TaskEither<
    UnexpectedError | AuthenticationError,
    InquiryServicePorts.ProviderIos.GetMany.Out
  > {
    return function_.pipe(
      Response.process(() => this.axios.get(URLS.getMany), GetMany.zRes),
      taskEither.flatMap((response) => {
        const { status, body } = response;
        if (status === 200) return taskEither.right(body.data.map(mapDtoToEntity));

        if (status === 401) return taskEither.left(new AuthenticationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }

  override getPaginated(
    params: InquiryServicePorts.ProviderIos.GetPaginated.In,
  ): taskEither.TaskEither<
    UnexpectedError | AuthenticationError,
    InquiryServicePorts.ProviderIos.GetPaginated.Out
  > {
    return function_.pipe(
      Response.process(
        () =>
          this.axios.post(
            URLS.getPaginated,
            Request.ListParams.mapListParams(params, GetPaginated.MAP_OF_FIELDS_TO_SORT_BY),
          ),
        GetPaginated.zRes,
      ),
      taskEither.flatMap((response) => {
        const { status, body } = response;
        if (status === 200) {
          const {
            data: { items, totalCount },
          } = body;
          const {
            success,
            data: pageMeta,
            error,
          } = Pagination.Meta.zSchema.safeParse({
            options: params.pagination,
            totalItems: totalCount,
          });

          if (!success) return taskEither.left(new UnexpectedError(error));

          return taskEither.right({
            items: items.map(mapDtoToEntity),
            meta: pageMeta,
          } satisfies InquiryServicePorts.ProviderIos.GetPaginated.Out);
        }

        if (status === 401) return taskEither.left(new AuthenticationError());

        return taskEither.left(new UnexpectedError(response));
      }),
    );
  }
}
export { ApiProvider };
